import React, { useState, useEffect } from 'react';
import { useBalance } from '../context/BalanceContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { Wallet, CheckCircle, Clock, XCircle, ChevronDown, Loader, Send } from 'lucide-react';

const WithdrawPage = () => {
  const { totalBalance, fetchBalance } = useBalance();
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState('withdraw');
  const [withdrawalHistory, setWithdrawalHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [selectedNetwork, setSelectedNetwork] = useState('TRC20');
  const [withdrawalAddress, setWithdrawalAddress] = useState('');
  const networks = ['TRC20', 'Ethereum (ERC20)', 'SOL', 'BSC (BEP20)', 'TON'];
  const [networkSelectOpen, setNetworkSelectOpen] = useState(false);
  const [completedRatingsCount, setCompletedRatingsCount] = useState(0);
  const [hasPreviousWithdrawals, setHasPreviousWithdrawals] = useState(false);

  const navigate = useNavigate();
  const { user } = useAuth();

  const formatDateTime = (dateString) => {
    const options = { year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return { text: 'Completed', color: 'text-green-400', bgColor: 'bg-green-500/10', icon: <CheckCircle size={14} /> };
      case 'rejected':
        return { text: 'Rejected', color: 'text-red-400', bgColor: 'bg-red-500/10', icon: <XCircle size={14} /> };
      case 'to be reviewed':
      case 'pending':
      default:
        return { text: 'Pending', color: 'text-yellow-400', bgColor: 'bg-yellow-500/10', icon: <Clock size={14} /> };
    }
  };

  useEffect(() => {
    if (activeTab === 'history' && user) {
      const fetchWithdrawalHistory = async () => {
        setHistoryLoading(true);
        setHistoryError(null);
        try {
          const token = localStorage.getItem('token');
          const config = {
            headers: { Authorization: `Bearer ${token}` },
          };
          const res = await axios.get('/api/withdrawals/my-history', config);
          setWithdrawalHistory(res.data);
          setHistoryLoading(false);
        } catch (err) {
          console.error('Error fetching withdrawal history:', err);
          setHistoryError('Failed to load withdrawal history.');
          setHistoryLoading(false);
        }
      };

      fetchWithdrawalHistory();
    }
  }, [activeTab, user]);

  useEffect(() => {
    fetchBalance();
    const fetchWithdrawInfo = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch('/api/profile/me', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (data.withdrawNetwork) setSelectedNetwork(data.withdrawNetwork);
          if (data.withdrawAddress) setWithdrawalAddress(data.withdrawAddress);
        }
      } catch (err) {
        // ignore
      }
    };
    fetchWithdrawInfo();

    // Fetch completed ratings count
    const fetchCompletedRatings = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get('/api/ratings/user', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const completed = res.data.filter(r => r.status === 'Completed').length;
        setCompletedRatingsCount(completed);
      } catch (err) {
        console.error('Error fetching ratings:', err);
      }
    };
    fetchCompletedRatings();

    // Check if user has previous withdrawals (only apply requirement to new users)
    const checkPreviousWithdrawals = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get('/api/withdrawals/my-history', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setHasPreviousWithdrawals(res.data.length > 0);
      } catch (err) {
        console.error('Error checking withdrawal history:', err);
      }
    };
    checkPreviousWithdrawals();
  }, [fetchBalance]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    if (parseFloat(amount) <= 0) {
      setError('Please enter a valid amount.');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      };
      const res = await axios.post(
        '/api/withdrawals/request',
        { amount: parseFloat(amount), network: selectedNetwork, address: withdrawalAddress },
        config
      );
      setSuccess(res.data.msg || 'Withdrawal request submitted successfully!');
      setAmount('');
      setWithdrawalAddress('');
      fetchBalance();
    } catch (err) {
      console.error('Error submitting withdrawal request:', err);
      setError(err.response?.data?.msg ? `Withdrawal failed: ${err.response.data.msg}` : 'An error occurred.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Helmet>
      <title>Withdraw USDT | RatingApp</title>
      <meta name="description" content="Withdraw your USDT earnings from RatingApp securely and instantly to your crypto wallet." />
    </Helmet>
    <div className="w-full pb-28 from-gray-900 via-blue-900/30 to-gray-900 text-white font-sans">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <motion.h1
          className="text-4xl font-extrabold tracking-tighter bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-500 text-center mb-2"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          Withdraw Funds
        </motion.h1>
        <p className="text-slate-400 text-center mb-8">Securely transfer USDT from your account.</p>

        {/* Tabs */}
        <motion.div
          className="text-white flex justify-center gap-2 md:gap-4 mb-8 bg-gray-800/20 backdrop-blur-xl border border-white/10 p-2 rounded-2xl"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <motion.button
            className={`w-full py-2.5 text-sm font-semibold rounded-lg transition-colors duration-300 relative ${activeTab === 'withdraw' ? 'text-white' : 'text-slate-400 hover:bg-white/5'}`}
            onClick={() => setActiveTab('withdraw')}
            whileTap={{ scale: 0.95 }}
          >
            Withdraw Now
            {activeTab === 'withdraw' && (
              <motion.div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg -z-10" layoutId="activeWithdrawTab" />
            )}
          </motion.button>
          <motion.button
            className={`w-full py-2.5 text-sm font-semibold rounded-lg transition-colors duration-300 relative ${activeTab === 'history' ? 'text-white' : 'text-slate-400 hover:bg-white/5'}`}
            onClick={() => setActiveTab('history')}
            whileTap={{ scale: 0.95 }}
          >
            Withdraw History
            {activeTab === 'history' && (
              <motion.div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg -z-10" layoutId="activeWithdrawTab" />
            )}
          </motion.button>
        </motion.div>

        <AnimatePresence mode="wait">
          {activeTab === 'withdraw' ? (
            <motion.div
              key="withdraw-form"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
            >
              <AnimatePresence>
                {error && (
                  <motion.p className="text-red-400 text-center mb-4 p-3 bg-red-500/10 rounded-lg" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>{error}</motion.p>
                )}
                {success && (
                  <motion.p className="text-green-400 text-center mb-4 p-3 bg-green-500/10 rounded-lg" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>{success}</motion.p>
                )}
              </AnimatePresence>
              
              <motion.div className="bg-gray-800/20 backdrop-blur-xl border border-white/10 rounded-2xl p-6 mb-8 text-center">
                <p className="text-slate-400 text-sm mb-1">Your Current Balance</p>
                <p className="text-4xl font-bold tracking-tight text-white flex items-center justify-center gap-2">
                  <Wallet size={32} className="text-cyan-400" />
                  {totalBalance.toFixed(2)}
                  <span className="text-2xl text-slate-400">USDT</span>
                </p>
              </motion.div>

              {!hasPreviousWithdrawals && completedRatingsCount < 16 && (
                <motion.div 
                  className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-6"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <p className="text-yellow-400 font-semibold text-sm mb-1">
                    ⚠️ Withdrawal Requirement
                  </p>
                  <p className="text-yellow-300/80 text-xs">
                    You must complete at least <span className="font-bold">16 app ratings</span> before you can withdraw funds.
                    <br />
                    You have completed <span className="font-bold">{completedRatingsCount} of 16</span> required ratings.
                  </p>
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="withdrawAmount" className="block text-sm font-medium text-slate-300 mb-2">Withdrawal Amount</label>
                  <input id="withdrawAmount" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                    placeholder="Enter amount"
                    className="w-full bg-gray-800/30 backdrop-blur-lg border border-white/10 rounded-xl py-3 px-4 text-white text-lg placeholder-slate-400 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="network" className="block text-sm font-medium text-slate-300 mb-2">Select Network</label>
                  <div className="relative">
                    <button type="button" onClick={() => setNetworkSelectOpen(!networkSelectOpen)} className="w-full flex items-center justify-between bg-gray-800/30 border border-white/10 rounded-xl px-4 py-3 text-left">
                      <span className="font-semibold">{selectedNetwork}</span>
                      <ChevronDown size={20} className={`transition-transform ${networkSelectOpen ? 'rotate-180' : ''}`} />
                    </button>
                    <AnimatePresence>
                      {networkSelectOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                          className="absolute z-10 w-full mt-1 bg-gray-800 border border-white/10 rounded-lg shadow-lg"
                        >
                          {networks.map(net => (
                            <div key={net} onClick={() => { setSelectedNetwork(net); setNetworkSelectOpen(false); }}
                              className="px-4 py-3 hover:bg-white/5 cursor-pointer"
                            >
                              {net}
                            </div>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                <div>
                  <label htmlFor="withdrawalAddress" className="block text-sm font-medium text-slate-300 mb-2">Withdrawal Address</label>
                  <input id="withdrawalAddress" type="text" value={withdrawalAddress} onChange={(e) => setWithdrawalAddress(e.target.value)}
                    placeholder="Enter withdrawal address"
                    className="w-full bg-gray-800/30 backdrop-blur-lg border border-white/10 rounded-xl py-3 px-4 text-white placeholder-slate-400 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all"
                    required
                  />
                </div>

                <motion.button 
                  type="submit" 
                  disabled={loading || (!hasPreviousWithdrawals && completedRatingsCount < 16)}
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold text-lg py-4 rounded-2xl shadow-lg shadow-cyan-500/20 border-t border-cyan-400/50 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  whileHover={(!hasPreviousWithdrawals && completedRatingsCount >= 16) || hasPreviousWithdrawals ? { scale: 1.02, boxShadow: '0 20px 30px -10px rgba(0, 255, 255, 0.25)' } : {}}
                  whileTap={((!hasPreviousWithdrawals && completedRatingsCount >= 16) || hasPreviousWithdrawals) ? { scale: 0.98 } : {}}
                >
                  {loading ? (
                    <><Loader size={20} className="animate-spin" /> Submitting...</>
                  ) : !hasPreviousWithdrawals && completedRatingsCount < 16 ? (
                    <>Complete {16 - completedRatingsCount} more rating{16 - completedRatingsCount !== 1 ? 's' : ''} to withdraw</>
                  ) : (
                    <><Send size={20}/> Submit Withdrawal Request</>
                  )}
                </motion.button>
              </form>
            </motion.div>
          ) : (
            <motion.div
              key="history"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-4"
            >
              {historyLoading && <div className="text-center py-8"><Loader className="animate-spin inline-block" /></div>}
              {historyError && <p className="text-red-400 text-center">{historyError}</p>}
              {!historyLoading && !historyError && withdrawalHistory.length === 0 && <p className="text-slate-400 text-center py-8">No withdrawal history found.</p>}
              
              {withdrawalHistory.map(w => {
                const status = getStatusColor(w.status);
                return (
                  <motion.div key={w._id}
                    className="bg-gray-800/20 backdrop-blur-xl border border-white/10 rounded-lg p-4"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-lg font-bold text-white">{w.amount.toFixed(2)} <span className="text-base text-slate-400">USDT</span></p>
                        <p className="text-xs text-slate-500">{new Date(w.createdAt).toLocaleString()}</p>
                      </div>
                      <div className={`flex items-center gap-2 text-xs font-medium px-2.5 py-1 rounded-full ${status.bgColor} ${status.color}`}>
                        {status.icon}
                        <span>{status.text}</span>
                      </div>
                    </div>
                    <div className="text-xs text-slate-400 mt-2 pt-2 border-t border-white/20 break-words">
                      To: {w.address} ({w.network})
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
    </>
  );
};

export default WithdrawPage;
